import { FC, useState, useEffect, useMemo } from 'react';
import { InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { help } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  FormContainer,
  Container,
  LeftColumn,
  HeaderRow,
  LabelTypography,
  Spacer24,
  ItemCard,
  SurchargeItemsWrapper,
  ItemRow,
  SelectBox,
  StyledAutocomplete,
  StyledAutocompleteTextField,
  ErrorTextSelect,
  AmountBox,
  StyledTextField,
  ErrorTextAmount,
  DeleteButton,
  StyledDeleteIcon,
  AddButton,
  AddSurchargeButtonContainer,
  StyledAddIcon,
  RightPanel,
  FinalTotalsTitle,
  TotalsRow,
  TotalLabel,
  TotalValue,
  SurchargeRow,
  GrandTotalBox,
  NextButtonContainer,
  BackButton,
  NextButton,
  ContentWrapper,
  DiscountLabelRed,
} from '../CreateOrder/Surcharges.styles';
import {
  ApplyDiscountHeader,
  DefaultLabel,
  StyledSwitch,
} from '../CreateOrder/Discounts.styles';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  EditOrderSurchargesProps,
  EditOrderSurcharge,
  EditOrderSurchargeItem,
  EditOrderSurchargeItemError,
} from 'src/models/EditOrder.interface';
import { getBasicSurcharges } from 'src/services/surcharges.service';
import { SurchargeLabelBlue } from '../CreateOrder/Summary.styles';
import { formatNumberWithCommas } from 'src/utils/format.utils';

const Surcharges: FC<EditOrderSurchargesProps> = ({
  onNext,
  onBack,
  orderId,
  orderData,
}) => {
  const [surcharges, setSurcharges] = useState<EditOrderSurcharge[]>([]);
  const [items, setItems] = useState<EditOrderSurchargeItem[]>([
    { surchargeId: '', amount: 0 },
  ]);
  const [applySurcharge, setApplySurcharge] = useState(false);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [netTotal, setNetTotal] = useState(0);
  const [itemsSubtotal, setItemsSubtotal] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [surchargesLoaded, setSurchargesLoaded] = useState(false);
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    fetchSurcharges();
    calculateNetTotal();
  }, []);

  useEffect(() => {
    if (surchargesLoaded && surcharges.length > 0 && !isInitialized) {
      initializeFormData();
    }
  }, [surchargesLoaded, surcharges.length, isInitialized]);

  // Recalculate percentage-based surcharges when items subtotal changes
  useEffect(() => {
    if (
      isInitialized &&
      items.length > 0 &&
      items.some((item) => (item as any).percentage !== undefined)
    ) {
      const { itemsSubtotal: currentItemsSubtotal } = calculateNetTotal();
      const newItems = items.map((item) => {
        if ((item as any).percentage !== undefined) {
          const percentageValue = (item as any).percentage;
          const calculatedAmount =
            (currentItemsSubtotal * percentageValue) / 100;
          return {
            ...item,
            amount: parseFloat(calculatedAmount.toFixed(2)),
          };
        }
        return item;
      });
      setItems(newItems);
      formik.setFieldValue('items', newItems);
    }
  }, [itemsSubtotal, isInitialized]);

  const initializeFormData = () => {
    const savedData = localStorage.getItem(`editOrderStep4_${orderId}`);
    let useLocalStorage = false;
    let parsedSavedData: {
      applySurcharge: boolean;
      items: EditOrderSurchargeItem[];
    } | null = null;

    if (savedData) {
      try {
        parsedSavedData = JSON.parse(savedData);
        if (parsedSavedData) {
          if (
            parsedSavedData.applySurcharge === true &&
            parsedSavedData.items &&
            parsedSavedData.items.length > 0
          ) {
            const allValid = parsedSavedData.items.every((item) => {
              if (!item.surchargeId || item.surchargeId === '') return false;
              return surcharges.some(
                (s) => String(s.id) === String(item.surchargeId)
              );
            });
            if (allValid) {
              useLocalStorage = true;
            }
          } else if (parsedSavedData.applySurcharge === false) {
            useLocalStorage = true;
          }
        }
      } catch (e) {
        useLocalStorage = false;
      }
    }

    let newApplySurcharge = false;
    let newItems: EditOrderSurchargeItem[] = [{ surchargeId: '', amount: 0 }];

    if (useLocalStorage && parsedSavedData) {
      newApplySurcharge = !!parsedSavedData.applySurcharge;
      newItems = parsedSavedData.items || [{ surchargeId: '', amount: 0 }];
    } else if (orderData?.surcharges && orderData.surcharges.length > 0) {
      newApplySurcharge = true;

      newItems = orderData.surcharges.map((orderSurcharge) => {
        let matchedSurcharge = surcharges.find(
          (s) => String(s.id) === String(orderSurcharge.surchargeId)
        );

        if (!matchedSurcharge && orderSurcharge.name) {
          matchedSurcharge = surcharges.find(
            (s) =>
              s.name?.trim().toLowerCase() ===
              orderSurcharge.name?.trim().toLowerCase()
          );
        }

        const surchargeId = matchedSurcharge ? matchedSurcharge.id : '';

        let amount = 0;
        let percentage: number | undefined;

        if (orderSurcharge.percentage) {
          percentage = parseFloat(orderSurcharge.percentage);
          const { itemsSubtotal: currentItemsSubtotal } = calculateNetTotal();
          amount = parseFloat(
            ((currentItemsSubtotal * percentage) / 100).toFixed(2)
          );
        } else if (orderSurcharge.amount !== null) {
          amount = orderSurcharge.amount;
        }

        return {
          surchargeId,
          surchargeName: orderSurcharge.name,
          amount,
          percentage,
        };
      });

      const dataToSave = {
        applySurcharge: newApplySurcharge,
        items: newItems,
        allSurcharges: surcharges,
      };
      localStorage.setItem(
        `editOrderStep4_${orderId}`,
        JSON.stringify(dataToSave)
      );
    }

    setApplySurcharge(newApplySurcharge);
    setItems(newItems);
    setIsInitialized(true);

    const step3Data = localStorage.getItem(`editOrderStep3_${orderId}`);
    if (step3Data) {
      const step3 = JSON.parse(step3Data);
      setApplyDiscount(step3.applyDiscount || false);
      setDiscountAmount(step3.discountAmount || 0);
    }
  };

  const fetchSurcharges = async () => {
    try {
      const data = await getBasicSurcharges();
      setSurcharges(data);
      setSurchargesLoaded(true);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setSurchargesLoaded(true);
    }
  };

  const calculateNetTotal = () => {
    const step2Data = localStorage.getItem(`editOrderStep2_${orderId}`);
    const step3Data = localStorage.getItem(`editOrderStep3_${orderId}`);

    let _itemsSubtotal = 0;
    let discountAmount = 0;

    if (step2Data) {
      const { items } = JSON.parse(step2Data);
      _itemsSubtotal = items.reduce(
        (sum: number, item: { quantity: number; unitPrice: number | string }) =>
          sum + item.quantity * Number(item.unitPrice),
        0
      );
    } else if (orderData) {
      _itemsSubtotal = orderData.items.reduce(
        (sum: number, item) => sum + item.quantity * Number(item.unitPrice),
        0
      );
    }

    if (step3Data) {
      const parsedStep3 = JSON.parse(step3Data);
      const { applyDiscount } = parsedStep3;
      if (applyDiscount) {
        discountAmount = parsedStep3.discountAmount || 0;
      }
    } else if (orderData) {
      //discountAmount = orderData.discountAmount || 0;
    }

    const calculatedNetTotal = _itemsSubtotal - discountAmount;
    setNetTotal(calculatedNetTotal);
    setItemsSubtotal(_itemsSubtotal);
    return { netTotal: calculatedNetTotal, itemsSubtotal: _itemsSubtotal };
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        items: applySurcharge
          ? Yup.array().of(
              Yup.object().shape({
                surchargeId: Yup.string().required(help.FIELD_REQUIRED),
                amount: Yup.number()
                  .positive('Amount must be greater than 0')
                  .required(help.VALUE_REQUIRED),
              })
            )
          : Yup.array(),
      }),
    [applySurcharge]
  );

  const formik = useFormik({
    initialValues: {
      items: items,
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Add surcharge names to each item
      const itemsWithNames = values.items.map(
        (item: EditOrderSurchargeItem) => {
          const surcharge = surcharges.find((s) => s.id === item.surchargeId);
          return {
            ...item,
            amount: Number(item.amount),
            surchargeName: surcharge?.name || item.surchargeName || '',
          };
        }
      );

      const dataToSave = {
        applySurcharge,
        items: itemsWithNames,
        allSurcharges: surcharges,
      };
      localStorage.setItem(
        `editOrderStep4_${orderId}`,
        JSON.stringify(dataToSave)
      );
      onNext();
    },
  });

  useEffect(() => {
    if (!isInitialized) return;

    if (applySurcharge && items.some((item) => !item.surchargeId)) return;

    // Add surcharge names to items
    const itemsWithNames = items.map((item: EditOrderSurchargeItem) => {
      const surcharge = surcharges.find((s) => s.id === item.surchargeId);
      return {
        ...item,
        surchargeName: surcharge?.name || item.surchargeName || '',
      };
    });

    const dataToSave = {
      applySurcharge,
      items: applySurcharge ? itemsWithNames : [{ surchargeId: '', amount: 0 }],
      allSurcharges: surcharges,
    };

    try {
      localStorage.setItem(
        `editOrderStep4_${orderId}`,
        JSON.stringify(dataToSave)
      );
    } catch (err) {
      // ignore storage errors
    }
  }, [applySurcharge, items, isInitialized, orderId]);

  useEffect(() => {
    formik.validateForm();
  }, [applySurcharge]);

  const handleAddItem = () => {
    const newItems = [...items, { surchargeId: '', amount: 0 }];
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      formik.setFieldValue('items', newItems);
    }
  };

  const handleSurchargeChange = (index: number, surchargeId: string) => {
    const selectedSurcharge = surcharges.find((s) => s.id === surchargeId);
    const newItems = [...items];
    newItems[index].surchargeId = surchargeId;

    const { itemsSubtotal: currentItemsSubtotal } = calculateNetTotal();

    if (selectedSurcharge) {
      if (selectedSurcharge.percentage) {
        const percentageValue = parseFloat(selectedSurcharge.percentage);
        const calculatedAmount = (currentItemsSubtotal * percentageValue) / 100;
        (newItems as any)[index].percentage = percentageValue;
        newItems[index].amount = parseFloat(calculatedAmount.toFixed(2));
      } else if (selectedSurcharge.defaultValue) {
        (newItems as any)[index].percentage = undefined;
        newItems[index].amount = parseFloat(selectedSurcharge.defaultValue);
      } else {
        (newItems as any)[index].percentage = undefined;
        newItems[index].amount = 0;
      }
    } else {
      newItems[index].amount = 0;
    }

    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const handleAmountChange = (index: number, amount: number | string) => {
    const newItems = [...items];
    newItems[index].amount = amount;
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const totalSurcharges = applySurcharge
    ? items.reduce((sum, item) => sum + Number(item.amount), 0)
    : 0;
  const grandTotal = netTotal + totalSurcharges;

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setApplySurcharge(checked);
    if (!checked) {
      const newItems = [{ surchargeId: '', amount: 0 }];
      setItems(newItems);
      formik.setFieldValue('items', newItems);
    }
  };

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <ContentWrapper>
        <Container>
          <LeftColumn>
            <ApplyDiscountHeader>
              <DefaultLabel>Apply Surcharges</DefaultLabel>
              <StyledSwitch
                checked={applySurcharge}
                onChange={handleToggleChange}
              />
            </ApplyDiscountHeader>
            {applySurcharge && (
              <>
                <HeaderRow>
                  <LabelTypography>Surcharge</LabelTypography>
                  <LabelTypography>Amount/Percentage</LabelTypography>
                  <Spacer24 />
                </HeaderRow>
                <SurchargeItemsWrapper>
                  <ItemCard>
                    {items.map((item, index) => {
                      const selectedSurcharge = surcharges.find(
                        (s) => s.id === item.surchargeId
                      );

                      return (
                        <ItemRow key={index}>
                          <SelectBox>
                            <StyledAutocomplete
                              options={surcharges.filter(
                                (surcharge) =>
                                  item.surchargeId === surcharge.id ||
                                  !items.some(
                                    (itm, idx) =>
                                      idx !== index &&
                                      itm.surchargeId === surcharge.id
                                  )
                              )}
                              getOptionLabel={(option) => option.name}
                              value={
                                surcharges.find(
                                  (s) => s.id === item.surchargeId
                                ) || null
                              }
                              onChange={(event, newValue) => {
                                handleSurchargeChange(
                                  index,
                                  newValue?.id || ''
                                );
                              }}
                              onBlur={formik.handleBlur}
                              ListboxProps={{
                                style: {
                                  maxHeight: '350px',
                                  overflow: 'auto',
                                },
                              }}
                              renderInput={(params) => (
                                <StyledAutocompleteTextField
                                  {...params}
                                  name={`items[${index}].surchargeId`}
                                  placeholder="Select type"
                                  hasValue={!!item.surchargeId}
                                  hasError={
                                    formik.touched.items?.[index]
                                      ?.surchargeId &&
                                    !!(
                                      formik.errors.items?.[
                                        index
                                      ] as EditOrderSurchargeItemError
                                    )?.surchargeId
                                  }
                                />
                              )}
                            />
                            {(formik.touched.items?.[index]?.surchargeId ||
                              formik.submitCount > 0) &&
                              (
                                formik.errors.items?.[
                                  index
                                ] as EditOrderSurchargeItemError
                              )?.surchargeId && (
                                <ErrorTextSelect>
                                  {
                                    (
                                      formik.errors.items?.[
                                        index
                                      ] as EditOrderSurchargeItemError
                                    )?.surchargeId
                                  }
                                </ErrorTextSelect>
                              )}
                          </SelectBox>
                          <AmountBox>
                            {selectedSurcharge &&
                            selectedSurcharge.percentage ? (
                              <>
                                <StyledTextField
                                  type="text"
                                  value={
                                    (item as any).percentage !== undefined
                                      ? (item as any).percentage
                                      : selectedSurcharge.percentage
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /,/g,
                                      ''
                                    );
                                    if (
                                      value === '' ||
                                      /^\d*\.?\d*$/.test(value)
                                    ) {
                                      const val = value === '' ? 0 : value;
                                      const numVal = Number(value);
                                      if (numVal > 100) {
                                        formik.setFieldError(
                                          `items[${index}].amount`,
                                          'Percentage cannot exceed 100'
                                        );
                                        return;
                                      }
                                      formik.setFieldError(
                                        `items[${index}].amount`,
                                        undefined
                                      );
                                      const newItems = [...items];
                                      (newItems as any)[index].percentage = val;
                                      const {
                                        itemsSubtotal: currentItemsSubtotal,
                                      } = calculateNetTotal();
                                      const calculatedAmount =
                                        (currentItemsSubtotal * numVal) / 100;
                                      newItems[index].amount = parseFloat(
                                        calculatedAmount.toFixed(2)
                                      );
                                      setItems(newItems);
                                      formik.setFieldValue('items', newItems);
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  error={
                                    (formik.touched.items?.[index]?.amount ||
                                      formik.submitCount > 0) &&
                                    Boolean(
                                      (
                                        formik.errors.items?.[
                                          index
                                        ] as EditOrderSurchargeItemError
                                      )?.amount
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        %
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                {(formik.touched.items?.[index]?.amount ||
                                  formik.submitCount > 0) &&
                                  (
                                    formik.errors.items?.[
                                      index
                                    ] as EditOrderSurchargeItemError
                                  )?.amount && (
                                    <ErrorTextAmount>
                                      {
                                        (
                                          formik.errors.items?.[
                                            index
                                          ] as EditOrderSurchargeItemError
                                        )?.amount
                                      }
                                    </ErrorTextAmount>
                                  )}
                              </>
                            ) : (
                              <>
                                <StyledTextField
                                  type="text"
                                  name={`items[${index}].amount`}
                                  value={
                                    item.amount
                                      ? formatNumberWithCommas(item.amount)
                                      : ''
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /,/g,
                                      ''
                                    );
                                    if (
                                      value === '' ||
                                      /^\d*\.?\d*$/.test(value)
                                    ) {
                                      handleAmountChange(
                                        index,
                                        value === '' ? 0 : value
                                      );
                                    }
                                  }}
                                  onBlur={formik.handleBlur}
                                  error={
                                    (formik.touched.items?.[index]?.amount ||
                                      formik.submitCount > 0) &&
                                    Boolean(
                                      (
                                        formik.errors.items?.[
                                          index
                                        ] as EditOrderSurchargeItemError
                                      )?.amount
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <i
                                          className="bx bx-money"
                                          aria-hidden="true"
                                        />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                {(formik.touched.items?.[index]?.amount ||
                                  formik.submitCount > 0) &&
                                  (
                                    formik.errors.items?.[
                                      index
                                    ] as EditOrderSurchargeItemError
                                  )?.amount && (
                                    <ErrorTextAmount>
                                      {
                                        (
                                          formik.errors.items?.[
                                            index
                                          ] as EditOrderSurchargeItemError
                                        )?.amount
                                      }
                                    </ErrorTextAmount>
                                  )}
                              </>
                            )}
                          </AmountBox>
                          {items.length > 1 && (
                            <DeleteButton
                              onClick={() => handleRemoveItem(index)}
                            >
                              <StyledDeleteIcon />
                            </DeleteButton>
                          )}
                        </ItemRow>
                      );
                    })}
                  </ItemCard>
                  <AddSurchargeButtonContainer>
                    <AddButton onClick={handleAddItem}>
                      <StyledAddIcon />
                      Add Surcharge
                    </AddButton>
                  </AddSurchargeButtonContainer>
                </SurchargeItemsWrapper>
              </>
            )}
          </LeftColumn>
          <RightPanel>
            <FinalTotalsTitle>Final Totals</FinalTotalsTitle>
            <TotalsRow>
              <TotalLabel>Items subtotal</TotalLabel>
              <TotalValue>
                {itemsSubtotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </TotalsRow>
            {applyDiscount && (
              <SurchargeRow>
                <DiscountLabelRed>Discount</DiscountLabelRed>
                <DiscountLabelRed>
                  -{' '}
                  {discountAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </DiscountLabelRed>
              </SurchargeRow>
            )}
            <TotalsRow>
              <TotalLabel>Net Total</TotalLabel>
              <TotalValue>
                {netTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </TotalsRow>
            <SurchargeRow>
              <SurchargeLabelBlue>Surcharges</SurchargeLabelBlue>
              <SurchargeLabelBlue>
                {totalSurcharges > 0 ? '+' : ''}{' '}
                {totalSurcharges.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </SurchargeLabelBlue>
            </SurchargeRow>
            <GrandTotalBox>
              <TotalLabel>Grand Total</TotalLabel>
              <TotalValue>
                {grandTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </GrandTotalBox>
          </RightPanel>
        </Container>
      </ContentWrapper>
      <NextButtonContainer>
        <BackButton onClick={onBack}>Back</BackButton>
        <NextButton type="submit">Next</NextButton>
      </NextButtonContainer>
    </FormContainer>
  );
};

export default Surcharges;
